import React from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard,
  Package,
  ShoppingCart,
  Users,
  FileUp,
  MessageSquare,
  Settings,
  LogOut,
  DollarSign,
  Sparkles,
  RefreshCw,
  UserCheck,
  Store,
  TrendingUp,
  FolderTree
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { useAuth } from '../../context/AuthContext';

const AdminLayout = () => {
  const navigate = useNavigate();
  const { admin, logout, isAuthenticated, loading } = useAuth();

  React.useEffect(() => {
    if (!loading && !isAuthenticated) {
      navigate('/admin');
    }
  }, [isAuthenticated, loading, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return null;
  }

  const navItems = [
    { path: '/admin/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
    { path: '/admin/products', icon: Package, label: 'Prodotti' },
    { path: '/admin/orders', icon: ShoppingCart, label: 'Ordini' },
    { path: '/admin/trending', icon: TrendingUp, label: 'In Evidenza' },
    { path: '/admin/categories', icon: FolderTree, label: 'Categorie' },
    { path: '/admin/affiliates', icon: UserCheck, label: 'Affiliati' },
    { path: '/admin/vendors', icon: Store, label: 'Venditori' },
    { path: '/admin/suppliers', icon: Users, label: 'Fornitori' },
    { path: '/admin/discovery', icon: Sparkles, label: 'Discovery' },
    { path: '/admin/import', icon: FileUp, label: 'Importa' },
    { path: '/admin/auto-update', icon: RefreshCw, label: 'Auto-Update' },
    { path: '/admin/tickets', icon: MessageSquare, label: 'Ticket' },
    { path: '/admin/pricing', icon: DollarSign, label: 'Prezzi' },
  ];

  const handleLogout = () => {
    logout();
    navigate('/admin');
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-layout">
      {/* Sidebar */}
      <aside className="fixed left-0 top-0 h-full w-64 bg-white border-r border-border flex flex-col z-50">
        {/* Logo */}
        <div className="p-6 pb-4">
          <h1 className="text-xl font-bold text-primary font-serif">QANTRI</h1>
          <p className="text-xs text-muted-foreground">Admin Dashboard</p>
        </div>

        {/* Navigation - scrollable */}
        <nav className="flex-1 overflow-y-auto px-6 space-y-1 pb-4">
          {navItems.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-colors ${
                  isActive
                    ? 'bg-primary/10 text-primary'
                    : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                }`
              }
              data-testid={`nav-${item.label.toLowerCase()}`}
            >
              <item.icon className="h-5 w-5" />
              {item.label}
            </NavLink>
          ))}
        </nav>

        {/* User Info & Logout - fixed at bottom */}
        <div className="p-6 pt-4 border-t bg-white">
          <div className="bg-muted/50 rounded-xl p-4 mb-3">
            <p className="font-medium text-sm truncate">{admin?.name}</p>
            <p className="text-xs text-muted-foreground truncate">{admin?.email}</p>
          </div>
          <Button
            variant="outline"
            className="w-full"
            onClick={handleLogout}
            data-testid="admin-logout"
          >
            <LogOut className="h-4 w-4 mr-2" />
            Esci
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="ml-64 p-8">
        <Outlet />
      </main>
    </div>
  );
};

export default AdminLayout;
